import { Link } from "react-router-dom";
import { getCoronaService } from "../content/coronaServices.js";
import { LOCAL_BUSINESS } from "../config/seo.js";
import NotFoundPage from "./NotFoundPage.jsx";

/**
 * Corona, CA service-area page. Mirrors the polished service-page layout (hero,
 * feature-card sections, process steps, CTA) using the shared brand styling, so
 * these pages read as real sales pages — not thin SEO doorways.
 *
 * Heading hierarchy: one H1 (hero), H2 per section, H3 for card titles.
 *
 * @param {{ slug: string }} props
 */
export default function ServiceAreaPage({ slug }) {
  const svc = slug ? getCoronaService(slug) : null;
  if (!svc) {
    return <NotFoundPage />;
  }

  const related = (svc.related || [])
    .map((s) => getCoronaService(s))
    .filter(Boolean);

  return (
    <article className="site-page site-page--wide svc-page">
      <div className="site-page__inner site-page__inner--wide">
        {/* Hero */}
        <header className="svc-hero">
          <p className="site-page__eyebrow">{svc.eyebrow}</p>
          <h1 className="site-page__title svc-hero__title">{svc.h1}</h1>
          <p className="site-page__lead svc-hero__lead">{svc.heroLead}</p>
          <ul className="svc-hero__points">
            {svc.heroPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="site-page__cta">
            <Link to="/contact" className="site-button-primary">
              Contact AICSNetworks
            </Link>
            {related.length > 0 ? (
              <a href="#related" className="site-button-ghost">
                View related services
              </a>
            ) : null}
          </p>
        </header>

        {/* Problem */}
        <section className="svc-section" aria-labelledby="svc-problem">
          <h2 id="svc-problem" className="svc-section__title">
            {svc.problemTitle}
          </h2>
          {svc.problem.map((p, i) => (
            <p key={i} className="svc-section__text">
              {p}
            </p>
          ))}
        </section>

        {/* Service scope */}
        <section className="svc-section" aria-labelledby="svc-scope">
          <h2 id="svc-scope" className="svc-section__title">
            {svc.scopeTitle}
          </h2>
          <div className="svc-grid">
            {svc.scope.map((card) => (
              <article key={card.title} className="svc-card">
                <h3 className="svc-card__title">{card.title}</h3>
                <p className="svc-card__body">{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Use cases */}
        <section className="svc-section" aria-labelledby="svc-usecases">
          <h2 id="svc-usecases" className="svc-section__title">
            {svc.useCasesTitle}
          </h2>
          <div className="svc-grid">
            {svc.useCases.map((card) => (
              <article key={card.title} className="svc-card svc-card--soft">
                <h3 className="svc-card__title">{card.title}</h3>
                <p className="svc-card__body">{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="svc-section" aria-labelledby="svc-process">
          <h2 id="svc-process" className="svc-section__title">
            How we work
          </h2>
          <ol className="svc-steps">
            {svc.process.map((step, i) => (
              <li key={step.title} className="svc-step">
                <span className="svc-step__num" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h3 className="svc-step__title">{step.title}</h3>
                  <p className="svc-step__body">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Local trust / service area */}
        <section className="svc-section svc-local" aria-labelledby="svc-local">
          <h2 id="svc-local" className="svc-section__title">
            {svc.localTitle}
          </h2>
          {svc.local.map((p, i) => (
            <p key={i} className="svc-section__text">
              {p}
            </p>
          ))}
          <p className="svc-local__nap">
            {LOCAL_BUSINESS.addressLocality}, {LOCAL_BUSINESS.addressRegion} ·{" "}
            <a href={`tel:${LOCAL_BUSINESS.telephone}`}>
              {LOCAL_BUSINESS.phoneDisplay}
            </a>{" "}
            · {LOCAL_BUSINESS.hoursDisplay}
          </p>
        </section>

        {/* Related services */}
        {related.length > 0 ? (
          <section className="svc-section" id="related" aria-labelledby="svc-related">
            <h2 id="svc-related" className="svc-section__title">
              Related services
            </h2>
            <div className="svc-grid">
              {related.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/services/${rel.slug}`}
                  className="svc-card svc-card--link"
                >
                  <h3 className="svc-card__title">
                    {rel.h1.replace(" in Corona, CA", "")}
                  </h3>
                  <span className="svc-card__more">Learn more →</span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* Final CTA */}
        <section className="svc-cta" aria-labelledby="svc-cta-heading">
          <h2 id="svc-cta-heading" className="svc-cta__title">
            Let&apos;s talk about your project
          </h2>
          <p className="svc-cta__body">
            Serving Corona, CA and nearby communities. Tell us what you&apos;re
            trying to accomplish and we&apos;ll help you scope it — no pressure.
          </p>
          <p className="site-page__cta svc-cta__actions">
            <Link to="/contact" className="site-button-primary">
              Contact AICSNetworks
            </Link>
            <Link to="/" className="site-button-ghost">
              ← Back to home
            </Link>
          </p>
        </section>
      </div>
    </article>
  );
}
